// eslint-disable-next-line import/order
import {
  CalendarDate,
  parseDate,
  parseZonedDateTime,
  ZonedDateTime,
} from '@internationalized/date';

import { enumUnit } from '@rematter/frontend-lib-api-client';

import { convertFromPounds, getUnitSymbol, Unit } from './weights';

type Maybe<T> = T | undefined | null;

const NUMBERS_PADDING = 6;
export const humanReadablePaddedNumber = (
  num?: Maybe<number | string>,
  thousandSeparator = '',
): string => {
  if (!num) {
    return '';
  }

  const paddedNum = num.toString().padStart(NUMBERS_PADDING, '0');

  if (thousandSeparator) {
    return paddedNum.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);
  }

  return paddedNum;
};

type Address = {
  streetAddress: string;
  streetAddress2?: string | null;
  city: string;
  state: string;
  postalCode: string;
};

export const humanReadableStringList = (list: string[], separator = ', ') =>
  list.filter(Boolean).join(separator);

export const humanReadableAddress = (address: Address) =>
  humanReadableStringList([
    address.streetAddress2
      ? `${address.streetAddress}, ${address.streetAddress2}`
      : address.streetAddress,
    address.city,
    address.state,
    address.postalCode,
  ]);

export const humanReadableShortPricingUnit = (unit?: string | null): string => {
  if (!unit) return '';

  const unitMap: Record<string, string> = {
    Pounds: 'lb',
    Ounces: 'oz',
    NetTons: 'NT',
    GrossTons: 'GT',
    Kilograms: 'kg',
    Grams: 'g',
    MetricTons: 'MT',
    ShortTons: 'ST',
    LongTons: 'LT',
    Each: 'ea',
    ShortHundredweight: 'CWT',
    LongHundredweight: 'CWT',
    Meter: 'm',
    Yard: 'yd',
  };

  return unitMap[unit] || unit;
};

export const pascalToSentenceCase = (str: string) =>
  str.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());

/**
 * Format a number with optional decimal places
 * @param value - The number to format
 * @param options - Formatting options
 * @returns Formatted number string
 */
export const humanReadableNumber = (
  value: number,
  options?: {
    decimalPlaces?: number;
    locale?: string;
  },
): string => {
  const decimalPlaces = options?.decimalPlaces ?? 3;
  return value.toLocaleString(options?.locale ?? 'en-US', {
    minimumFractionDigits: value % 1 === 0 ? 0 : decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  });
};

/**
 * Format weight with unit
 * @param weightInPounds - Weight value in pounds
 * @param options - Formatting options
 * @returns Formatted weight string with unit
 */
export const humanReadableWeight = (
  weightInPounds: number,
  options?: {
    unit?: Unit;
    decimalPlaces?: number;
    includeUnit?: boolean;
    convert?: boolean;
    locale?: string;
  },
): string => {
  const unit = options?.unit ?? enumUnit.Pounds;
  const decimalPlaces = options?.decimalPlaces ?? 3;
  const includeUnit = options?.includeUnit ?? true;
  const convert = options?.convert ?? true;
  const locale = options?.locale ?? 'en-US';

  const convertedValue = convert
    ? convertFromPounds(weightInPounds, unit)
    : weightInPounds;

  // Round the value to check if decimals are meaningful
  const powerOf10 = 10 ** decimalPlaces;
  const hasMeaningfulDecimals =
    Math.round(convertedValue * powerOf10) % powerOf10 !== 0;

  const formattedValue = convertedValue.toLocaleString(locale, {
    minimumFractionDigits: hasMeaningfulDecimals ? decimalPlaces : 0,
    maximumFractionDigits: decimalPlaces,
  });

  if (!includeUnit) {
    return formattedValue;
  }

  return `${formattedValue} ${getUnitSymbol(unit)}`;
};

/**
 * Format currency amount
 * @param amount - The amount to format
 * @param options - Formatting options
 * @returns Formatted currency string
 */
export const humanReadableCurrency = (
  amount: number,
  options?: {
    locale?: string;
    currencyCode?: string;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
    includeSymbol?: boolean;
  },
): string => {
  const locale = options?.locale ?? 'en-US';
  const currencyCode = options?.currencyCode ?? 'USD';
  const includeSymbol = options?.includeSymbol ?? true;

  if (!includeSymbol) {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: options?.minimumFractionDigits ?? 2,
      maximumFractionDigits: options?.maximumFractionDigits ?? 2,
    }).format(amount);
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: options?.minimumFractionDigits ?? 2,
    maximumFractionDigits: options?.maximumFractionDigits ?? 2,
  }).format(amount);
};

/**
 * Format a number as a percentage
 * @param value - The value to format (e.g., 50 for 50%, not 0.5)
 * @param options - Formatting options
 * @returns Formatted percentage string
 */
export const humanReadablePercent = (
  value: number,
  options?: {
    locale?: string;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  },
): string => {
  const locale = options?.locale ?? 'en-US';
  const minDigits = options?.minimumFractionDigits ?? 1;
  const maxDigits = options?.maximumFractionDigits ?? 1;

  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: minDigits,
    maximumFractionDigits: maxDigits,
  }).format(value / 100);
};

/**
 * Format value based on type
 * @param value - The value to format
 * @param type - The type of value ('number', 'weight', 'currency')
 * @param options - Type-specific formatting options
 * @returns Formatted string
 */
export const humanReadableValue = (
  value: number,
  type: 'number' | 'weight' | 'currency',
  options?: {
    // For weight
    unit?: Unit;
    decimalPlaces?: number;
    includeUnit?: boolean;
    // For currency
    currencyCode?: string;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
    includeSymbol?: boolean;
  },
): string => {
  switch (type) {
    case 'number':
      return humanReadableNumber(value, options);

    case 'weight':
      return humanReadableWeight(value, options);

    case 'currency':
      if (!options?.currencyCode) {
        throw new Error('Currency code is required for currency formatting');
      }
      return humanReadableCurrency(value, {
        currencyCode: options.currencyCode,
        minimumFractionDigits: options.minimumFractionDigits,
        maximumFractionDigits: options.maximumFractionDigits,
        includeSymbol: options?.includeSymbol,
      });

    default:
      return value.toString();
  }
};

/**
 * Format a date range
 * @param start - Start date (Date, string, or ZonedDateTime)
 * @param end - End date (Date, string, or ZonedDateTime)
 * @param options - Formatting options
 * @returns Formatted date range string
 */
export const humanReadableDateRange = (
  start: Date | string | CalendarDate,
  end: Date | string | CalendarDate,
  options?: {
    locale?: string;
    timezone?: string;
    dateStyle?: 'full' | 'long' | 'medium' | 'short';
  },
): string => {
  const locale = options?.locale ?? 'en-US';
  const timezone = options?.timezone ?? 'UTC';
  const dateStyle = options?.dateStyle ?? 'medium';

  const formatDate = (date: Date | string | CalendarDate): string => {
    let dateObj: Date;

    if (typeof date === 'string') {
      // Parse ISO 8601 string with timezone to ZonedDateTime first
      const calendarDate = parseDate(date);
      dateObj = calendarDate.toDate(timezone);
    } else if (date instanceof Date) {
      dateObj = date;
    } else {
      dateObj = date.toDate(timezone);
    }

    // Validate the date before formatting
    if (!dateObj || isNaN(dateObj.getTime())) {
      return 'Invalid Date';
    }

    return new Intl.DateTimeFormat(locale, { dateStyle }).format(dateObj);
  };

  return `${formatDate(start)} - ${formatDate(end)}`;
};

// === File Size Formatting ===

export interface HumanReadableFileSizeOptions {
  /** Locale for number formatting (default: 'en-US') */
  locale?: string;
  /** Override decimal places (default: 0 for bytes, 1 for KB+) */
  decimalPlaces?: number;
  /** Placeholder for null/undefined values (default: '-') */
  nullPlaceholder?: string;
}

/**
 * Formats byte count into human-readable file size.
 *
 * @example
 * humanReadableFileSize(1024) // "1.0 KB"
 * humanReadableFileSize(1536, { decimalPlaces: 2 }) // "1.50 KB"
 * humanReadableFileSize(null) // "-"
 */
export function humanReadableFileSize(
  bytes: number | null | undefined,
  options: HumanReadableFileSizeOptions = {},
): string {
  const { decimalPlaces, nullPlaceholder = '-' } = options;

  if (bytes == null || bytes === 0) return nullPlaceholder;

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const size = bytes / Math.pow(1024, i);
  const decimals = decimalPlaces ?? (i === 0 ? 0 : 1);

  return `${size.toFixed(decimals)} ${units[i]}`;
}

// === Date Formatting ===

export interface HumanReadableDateOptions {
  /** Locale for date formatting (default: 'en-US') */
  locale?: string;
  /** Date formatting options */
  dateOptions?: Intl.DateTimeFormatOptions;
  /** Placeholder for invalid dates (default: '-') */
  nullPlaceholder?: string;
}

/**
 * Formats a date value into localized date string.
 *
 * @example
 * humanReadableDate(new Date()) // "12/1/2025"
 * humanReadableDate("2025-12-01", { locale: 'en-GB' }) // "01/12/2025"
 * humanReadableDate(null) // "-"
 */
export function humanReadableDate(
  date: unknown,
  options: HumanReadableDateOptions = {},
): string {
  const { locale = 'en-US', dateOptions, nullPlaceholder = '-' } = options;

  if (!date) return nullPlaceholder;

  try {
    const dateObj = new Date(String(date));
    if (isNaN(dateObj.getTime())) return nullPlaceholder;
    return dateObj.toLocaleDateString(locale, dateOptions);
  } catch {
    return nullPlaceholder;
  }
}

/**
 * Formats a ZonedDateTime or ISO string into a compact date and time string.
 *
 * @example
 * humanReadableDateTime(zonedDateTime) // "Jul 23・11:34 AM"
 * humanReadableDateTime("2024-07-23T11:34:00-05:00") // "Jul 23・11:34 AM"
 * humanReadableDateTime(zonedDateTime, { includeYear: true }) // "Jul 23, 2024・11:34 AM"
 * humanReadableDateTime(zonedDateTime, { includeSeconds: true }) // "Jul 23・11:34:56 AM"
 */
export function humanReadableDateTime(
  dateTime: ZonedDateTime | string,
  options?: {
    locale?: string;
    separator?: string;
    includeYear?: boolean;
    includeSeconds?: boolean;
  },
): string {
  const locale = options?.locale ?? 'en-US';
  const separator = options?.separator ?? '・';
  const includeYear = options?.includeYear ?? false;
  const includeSeconds = options?.includeSeconds ?? false;

  // Parse string to ZonedDateTime if needed, then convert to Date
  const dateObj =
    typeof dateTime === 'string'
      ? parseZonedDateTime(dateTime).toDate()
      : dateTime.toDate();

  const dateFormatter = new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    ...(includeYear && { year: 'numeric' }),
  });

  const timeFormatter = new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
    ...(includeSeconds && { second: '2-digit' }),
    hour12: true,
  });

  const datePart = dateFormatter.format(dateObj);
  const timePart = timeFormatter.format(dateObj);

  return `${datePart}${separator}${timePart}`;
}
